<template>
  <!-- CD-22 Layby Cancellation -->
  <Dialog :open="open" @update:open="$emit('update:open', $event)">
    <DialogContent class="max-w-[450px] rounded-xl" @pointerDownOutside.prevent @escapeKeyDown.prevent>
      <DialogHeader>
        <DialogTitle class="text-[18px] font-bold text-grey-light-900">Dispute Cancellation</DialogTitle>
        <DialogDescription class="text-[14px] text-grey-light-500">
          Send your dispute message regarding the cancellation fee.
        </DialogDescription>
      </DialogHeader>
      <textarea
        v-model="disputeMessage"
        placeholder="Type dispute message..."
        class="w-full min-h-[150px] rounded-lg border border-grey-light-200 p-3 text-[14px] resize-none focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-300"
      />
      <div class="flex gap-3">
        <Button variant="outline" class="flex-1" @click="close">Cancel</Button>
        <Button
          class="flex-1 bg-teal-600 hover:bg-teal-700"
          :disabled="!disputeMessage.trim() || isSending"
          @click="sendDispute"
        >
          <Loader2 v-if="isSending" class="h-4 w-4 animate-spin mr-2" />
          Send
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { NetworkError } from '@/errors';
import { updateOrder } from '@/services/orders';
import { useUserStore } from '@/stores/users';
import dayjs from '@/utils/dayjs.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { Loader2 } from 'lucide-vue-next';
import { ref, watch } from 'vue';
import type { ModelOrderResponse } from '@layup/api-sdk';

const props = defineProps<{
  open: boolean;
  order?: ModelOrderResponse;
  cancellationReason: string;
}>();

const emit = defineEmits<{
  'update:open': [value: boolean];
  disputed: [];
}>();

const { user } = useUserStore();
const disputeMessage = ref('');
const isSending = ref(false);

const close = () => emit('update:open', false);

const sendDispute = async () => {
  if (!props.order?._id || !disputeMessage.value.trim()) return;

  isSending.value = true;
  try {
    const notifications = [...(props.order.notifications ?? [])];
    const lastNote = notifications.at(-1);
    if (lastNote && lastNote.userId !== user?._id) {
      lastNote.read = true;
    }

    notifications.push({
      change: 'DISPUTE',
      date: dayjs().format('Do MMM YYYY, h:mma'),
      name: user?.name ?? '',
      reason: props.cancellationReason,
      details: disputeMessage.value,
      read: false,
      userId: user?._id,
      type: 'CANCELLATION',
    });

    await updateOrder(props.order._id, {
      orderCancellationRequest: 'DISPUTE',
      notifications,
    });

    addMetric('cancellation_request', { status: 'success', channel: 'shopper', action: 'dispute' });
    close();
    emit('disputed');
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('cancellation_request', { status: 'network_error', channel: 'shopper', action: 'dispute' });
    } else {
      createException(error);
      addMetric('cancellation_request', { status: 'error', channel: 'shopper', action: 'dispute' });
    }
  } finally {
    isSending.value = false;
  }
};

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) disputeMessage.value = '';
  },
);
</script>
